import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import { DelayedSpinner } from "../DelayedSpinner/DelayedSpinner";
import { endpoints } from "../../endpoints";
import type { ReferenceEntityMappingSchema } from "../../types/types.PIM";
import { rowHover, useStoreState } from "../../util/util";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "../Table/Table";
import { SectionTitle } from "../Form/Form";

const MappingSection = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const MappingTableWrapper = styled.div`
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  overflow: hidden;
`;

type MappingRow = {
  attributeName: string;
  attributeType: string;
  referenceEntityName: string;
  referenceEntityAttribute: string;
};

const formatAttributeType = (type: string) =>
  type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

type ReferenceEntityMappingTableProps = {
  contextType: "collection" | "group";
  contextId: string;
  showTitle?: boolean;
};

export function ReferenceEntityMappingTable({
  contextType,
  contextId,
  showTitle = true,
}: ReferenceEntityMappingTableProps): JSX.Element | null {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();

  const endpoint =
    contextType === "collection"
      ? endpoints.v2_storefronts_id_pim_collections_id_reference_entity_mappings(
          storefront_id,
          contextId
        )
      : endpoints.v2_storefronts_id_pim_groups_id_reference_entity_mappings(
          storefront_id,
          contextId
        );

  const { data, error, isValidating } =
    useSWR<ReferenceEntityMappingSchema>(endpoint);

  const tableColumns = React.useMemo<ColumnDef<MappingRow>[]>(
    () => [
      { header: t("Attribute Name"), accessorKey: "attributeName" },
      {
        header: t("Attribute Type"),
        accessorKey: "attributeType",
        cell: ({ getValue }) =>
          formatAttributeType((getValue() as string) ?? ""),
      },
      {
        header: t("Reference Entity"),
        accessorKey: "referenceEntityName",
      },
      {
        header: t("Reference Entity Attribute"),
        accessorKey: "referenceEntityAttribute",
      },
    ],
    [t]
  );

  if (isValidating && !data) {
    return (
      <MappingSection>
        <DelayedSpinner />
      </MappingSection>
    );
  }

  if (error || !data) {
    return null;
  }

  if (
    !data.has_mapping ||
    !data.feature_enabled ||
    data.mappings.length === 0
  ) {
    return null;
  }

  // Build mapping rows: one row per mapped attribute across all mappings
  const mappingRows = data.mappings.flatMap((mapping) =>
    mapping.mapped_attributes.map((attr) => ({
      attributeName: attr.attribute_display_name || attr.attribute_name,
      attributeType: attr.attribute_input_type,
      referenceEntityName:
        mapping.reference_entity.display_name || mapping.reference_entity.name,
      referenceEntityAttribute: attr.column_display_name || attr.column_name,
    }))
  );

  if (mappingRows.length === 0) {
    return null;
  }

  return (
    <MappingSection>
      {showTitle && (
        <SectionTitle>{t("Reference Entity Mapping")}</SectionTitle>
      )}
      <MappingTableWrapper>
        <Table
          columns={tableColumns}
          data={mappingRows}
          isLoading={isValidating}
          error={error ?? undefined}
          rowHover={rowHover}
          lastChildleftAlign={true}
        />
      </MappingTableWrapper>
    </MappingSection>
  );
}
